import {BaSeButton, BaSeCheckbox, BaSeIcon} from "@base/react";
import {forwardRef, useEffect, useImperativeHandle, useRef, useState} from "react";
import FormBuilder from "../../components/form-builder/form-builder";
import FormInput from "../../components/form-builder/form-input";
import * as Yup from "yup";
import ApiPlannings from "../../services/planning";
import {toast, ToastContainer} from "react-toastify";
import iconError from "../../assets/images/icons/exclamation-octagon.png";
import {eventTag, getCurrentCNPJ} from "../../services/utils";
import ApiGeneral from "../../services/general";
import {useApp} from "../../hooks/use-app";
import './index.scss';

const apiPlannings = ApiPlannings();
const eventBalanceSheet = {
  name: "balance_sheet",
  category: "balance_sheet",
  action: "click",
}
const BP_SLUG_BY_KEY = { pmr: 'bp_receivable', pp: 'bp_payable', ge: 'bp_inventory' };

const BusinessAccordion = forwardRef(({
  step,
  planId,
  balanceValues,
  onNext,
  disabledFields,
  loading,
  forceUpdateForm,
}, ref) => {
  const { companies } = useApp()

  const currentCnpj = getCurrentCNPJ(companies) || null
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCheckbox, setSelectedCheckbox] = useState(null);
  const [selectedInput, setSelectedInput] = useState(null);
  const [lastSelectedInput, setLastSelectedInput] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const hasInitialized = useRef(false);
  const prevAutoValuesRef = useRef(null);
  
  const [autoValues, setAutoValues] = useState({
    prazo_medio: '',
    valor_estimado: ''
  });
  
  const [manualValues, setManualValues] = useState({
    prazo_medio: '',
    valor_estimado: ''
  });

  const stepMapping = {
    5: {
      upperText: 'recebimento',
      lowerText: 'a receber',
      useAutoKey: 'pmr_use_auto',
      daysKey: 'pmr_days_manual',
      valueKey: 'pmr_value_manual',
      balanceKey: 'pmr',
    },
    6: {
      upperText: 'pagamento',
      lowerText: 'a pagar',
      useAutoKey: 'pp_use_auto',
      daysKey: 'pp_days_manual',
      valueKey: 'pp_value_manual',
      balanceKey: 'pp',
    },
    7: {
      upperText: 'estocagem',
      lowerText: 'em estoque (a preço de custo)',
      useAutoKey: 'ge_use_auto',
      daysKey: 'ge_days_manual',
      valueKey: 'ge_value_manual',
      balanceKey: 'ge',
    }
  };
  
  const currentMapping = stepMapping[step];

  const validationSchema = Yup.object().shape({
    prazo_medio: Yup.string().when('$selectedInput', {
      is: 'days',
      then: (schema) => schema.required('Campo obrigatório'),
      otherwise: (schema) => schema.nullable()
    }),
    valor_estimado: Yup.string().when('$selectedInput', {
      is: 'value',
      then: (schema) => schema.required('Campo obrigatório'),
      otherwise: (schema) => schema.nullable()
    })
  });

  const mapLastFieldToInput = (lastField) => {
    if (!lastField) return null;
    if (lastField.includes('_days_')) return 'days';
    if (lastField.includes('_value_')) return 'value';
    return null;
  };

  const formatBalanceData = (data) => {
    return {
      prazo_medio: data?.days ? parseInt(data.days, 10).toString() : '',
      valor_estimado: data?.value ? data.value.toFixed(2).toString() : '',
    };
  };

  const hasAutoValuesChanged = (newAutoData) => {
    const prevAuto = prevAutoValuesRef.current;
    
    if (!prevAuto || !newAutoData) return true;
    
    return (
      newAutoData.days !== prevAuto.days ||
      newAutoData.value !== prevAuto.value
    );
  };

  useEffect(() => {
    if (!balanceValues || !currentMapping.balanceKey) return;

    const autoData = balanceValues.auto?.[currentMapping.balanceKey];
    const manualData = balanceValues.manual?.[currentMapping.balanceKey];

    if (!autoData) return;

    const formattedAutoValues = formatBalanceData(autoData);
    const formattedManualValues = formatBalanceData(manualData);

    const autoChanged = hasAutoValuesChanged(autoData);

    if (!hasInitialized.current) {
      setAutoValues(formattedAutoValues);
      prevAutoValuesRef.current = { ...autoData };
      
      const hasManualData = manualData !== null && manualData !== undefined;
      
      if (hasManualData) {
        setSelectedCheckbox(false);
        setManualValues(formattedManualValues);
        
        const inputFromLastField = mapLastFieldToInput(manualData.last_field);
        setSelectedInput(inputFromLastField);
        setLastSelectedInput(inputFromLastField);
      } else {
        setSelectedCheckbox(true);
        setManualValues(formattedAutoValues);
        setSelectedInput(null);
        setLastSelectedInput(null);
      }
      
      hasInitialized.current = true;
    } else if (autoChanged) {
      setAutoValues(formattedAutoValues);
      prevAutoValuesRef.current = { ...autoData };
      
      setSelectedCheckbox(true);
      setManualValues(formattedAutoValues);
      setSelectedInput(null);
      setLastSelectedInput(null);
    }
  }, [balanceValues, step, currentMapping.balanceKey]);

  const currentFormValues = selectedCheckbox === true ? autoValues : manualValues;

  const invalidForm = (overrideValues = {}) => {
    const values = { ...manualValues, ...overrideValues };
    
    if (selectedCheckbox === null) return true;
    if (selectedCheckbox === false && selectedInput === null) return true;
    if (selectedCheckbox === false) {
      if (selectedInput === 'days' && !values.prazo_medio) return true;
      if (selectedInput === 'value' && !values.valor_estimado) return true;
    }
    return false;
  };

  const buildPayload = (overrideValues = {}) => {
    const values = { ...manualValues, ...overrideValues };
    const payload = { plan: planId };

    if (selectedCheckbox === true) {
      payload[currentMapping.useAutoKey] = true;
      payload[currentMapping.daysKey] = null;
      payload[currentMapping.valueKey] = null;
    } else if (selectedCheckbox === false) {
      payload[currentMapping.useAutoKey] = false;

      if (selectedInput === 'days') {
        payload[currentMapping.daysKey] = values.prazo_medio || null;
        payload[currentMapping.valueKey] = null;
      } else if (selectedInput === 'value') {
        payload[currentMapping.daysKey] = null;
        const cleanValue = values.valor_estimado
          .slice(2)
          .replace(/\./g, '')
          .replace(',', '.');
        payload[currentMapping.valueKey] = cleanValue || null;
      }
    }
    return payload;
  };

  const resetToAutomatic = async (continueAfter) => {
    setSelectedCheckbox(true);
    setSelectedInput(null);
    setLastSelectedInput(null);

    const payload = { plan: planId };
    payload[currentMapping.useAutoKey] = true;
    payload[currentMapping.daysKey] = null;
    payload[currentMapping.valueKey] = null;

    setIsSaving(true);
    try {
      await apiPlannings.updateBalanceAdjustments(payload);
      eventTag(eventBalanceSheet?.name, eventBalanceSheet?.category, "submit", "Atualizando balanço patrimonial")
      await forceUpdateForm();
      if (continueAfter) {
        onNext();
      }
    } catch (error) {
      console.error(error);
      toast.error(
        <ToastContainer
          title="Falha!"
          description="Houve uma falha no sistema. Tente novamente."
        />,
        {
          icon: () => <img src={iconError} alt="icon" />,
          autoClose: 5000,
          pauseOnFocusLoss: false,
        }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async (continueAfter = false, overrideValues = {}) => {
    if (selectedCheckbox === false && selectedInput === null) {
      await resetToAutomatic(continueAfter);
      return;
    }

    if (selectedCheckbox === false && selectedInput !== null) {
      const values = { ...manualValues, ...overrideValues };
      const hasValue = (selectedInput === 'days' && values.prazo_medio) || 
                       (selectedInput === 'value' && values.valor_estimado);
      
      if (!hasValue) {
        await resetToAutomatic(continueAfter);
        return;
      }
    }

    if (invalidForm(overrideValues)) return;

    setIsSaving(true);
    try {
      const payload = buildPayload(overrideValues);
      const response = await apiPlannings.updateBalanceAdjustments(payload);
      eventTag(eventBalanceSheet?.name, eventBalanceSheet?.category, "submit", "Atualizando balanço patrimonial")
      await forceUpdateForm();
      
      if (response.data?.balance_values && currentMapping.balanceKey) {
        const responseAutoData = response.data.balance_values.auto?.[currentMapping.balanceKey];
        const responseManualData = response.data.balance_values.manual?.[currentMapping.balanceKey];

        if (responseAutoData) {
          const formattedAutoValues = formatBalanceData(responseAutoData);
          setAutoValues(formattedAutoValues);
          prevAutoValuesRef.current = { ...responseAutoData };
        }

        if (responseManualData && selectedCheckbox === false) {
          const formattedManualValues = formatBalanceData(responseManualData);
          setManualValues(formattedManualValues);
        }
      }

      if (continueAfter) {
        onNext();
      }
    } catch (error) {
      console.error(error);
      toast.error(
        <ToastContainer
          title="Falha!"
          description="Houve uma falha no sistema. Tente novamente."
        />,
        {
          icon: () => <img src={iconError} alt="icon" />,
          autoClose: 5000,
          pauseOnFocusLoss: false,
        }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleFormChange = (fieldName, value) => {
    setManualValues({
      ...manualValues,
      [fieldName]: value
    });
  };

  const handleInputBlur = async (fieldName, value) => {
    if (selectedCheckbox === false && 
        ((selectedInput === 'days' && fieldName === 'prazo_medio') ||
         (selectedInput === 'value' && fieldName === 'valor_estimado'))) {
      if (value) {
        await handleSave(false, { [fieldName]: value });
      }
    }
  };

  const handleCheckboxChange = async (value) => {
    setSelectedCheckbox(value);
    eventTag(eventBalanceSheet?.name, eventBalanceSheet?.category, eventBalanceSheet?.action, `valores correspondem à realidade: ${value ? 'Sim' : 'Não'}`)

    if (value === true) {
      setSelectedInput(null);
      const payload = { plan: planId };
      payload[currentMapping.useAutoKey] = true;
      payload[currentMapping.daysKey] = null;
      payload[currentMapping.valueKey] = null;

      setIsSaving(true);
      try {
        await apiPlannings.updateBalanceAdjustments(payload);
        eventTag(eventBalanceSheet?.name, eventBalanceSheet?.category, "submit", "Atualizando balanço patrimonial")
        await forceUpdateForm();
      } catch (error) {
        console.error(error);
        toast.error(
          <ToastContainer
            title="Falha!"
            description="Houve uma falha no sistema. Tente novamente."
          />,
          {
            icon: () => <img src={iconError} alt="icon" />,
            autoClose: 5000,
            pauseOnFocusLoss: false,
          }
        );
      } finally {
        setIsSaving(false);
      }
    } else {
      const inputToRestore = lastSelectedInput || null;
      setSelectedInput(inputToRestore);
      
      if (!manualValues.prazo_medio && !manualValues.valor_estimado) {
        setManualValues(autoValues);
      }
    }
  };

  const handleInputSelection = (inputType) => {
    setSelectedInput(inputType);
    setLastSelectedInput(inputType);
  };

  useImperativeHandle(ref, () => ({
    handleSave: (continueAfter = false) => handleSave(continueAfter)
  }))


  return (
    <div className="business-accordion">
      <div
        className={`custom-accordion-header ${isOpen ? 'active' : ''}`}
        onClick={() => {
          setIsOpen(!isOpen)
          eventTag(eventBalanceSheet?.name, eventBalanceSheet?.category, eventBalanceSheet?.action, `${isOpen ? 'Fechar' : 'Abrir'} balanço patrimonial`)
        }}
      >
        <div className="custom-accordion-title">
          <BaSeIcon
            color="#2E5DE6"
            description="info"
            name="info-circle"
            size={32}
          />
          <span>Esse resultado corresponde à realidade do seu negócio?</span>
        </div>
        <span className="custom-accordion-arrow">
          <figure
            style={{
              transform: `scale(${isOpen ? "-1" : "1"})`,
              transition: "transform 0.2s ease",
            }}
          >
            <BaSeIcon
              color="#617385"
              description="Toggle"
              name="system/arrow-down-s-line"
              size={24}
            />
          </figure>
        </span>
      </div>

      <div className={`custom-accordion-content ${isOpen ? 'active' : ''}`}>
        <div className="accordion-content">
          <div className="accordion-content-left">
            <p>Os valores acima correspondem à realidade do seu negócio?</p>
            <div className="accordion-checkboxes">
              <BaSeCheckbox
                shape="square"
                label="Sim"
                checked={selectedCheckbox === true}
                disabled={disabledFields}
                onChange={() => handleCheckboxChange(true)}
              />
              <BaSeCheckbox
                shape="square"
                label="Não"
                checked={selectedCheckbox === false}
                disabled={disabledFields}
                onChange={() => handleCheckboxChange(false)}
              />
            </div>
          </div>

          <div className="accordion-content-right">
            <FormBuilder
              initialValues={currentFormValues}
              validationSchema={validationSchema}
              context={{ selectedInput }}
              enableReinitialize
            >
              <div className="input-wrapper">
                {selectedCheckbox === false && (
                  <BaSeCheckbox
                    shape="circle"
                    checked={selectedInput === 'days'}
                    disabled={disabledFields}
                    onChange={() => handleInputSelection('days')}
                  />
                )}
                <FormInput
                  name="prazo_medio"
                  type="number"
                  label={`Prazo médio de ${currentMapping.upperText} em dias`}
                  isDisabled={selectedCheckbox !== false || selectedInput !== 'days' || disabledFields}
                  onExternalChange={handleFormChange}
                  onExternalBlur={handleInputBlur}
                  maxValue={99999}
                />
              </div>
              <div className="input-wrapper">
                {selectedCheckbox === false && (
                  <BaSeCheckbox
                    shape="circle"
                    checked={selectedInput === 'value'}
                    disabled={disabledFields}
                    onChange={() => handleInputSelection('value')}
                  />
                )}
                <FormInput
                  name="valor_estimado"
                  type="currency"
                  label={`Valor estimado ${currentMapping.lowerText}`}
                  isDisabled={selectedCheckbox !== false || selectedInput !== 'value' || disabledFields}
                  onExternalChange={handleFormChange}
                  onExternalBlur={handleInputBlur}
                />
              </div>
            </FormBuilder>
            <div className="button-wrapper">
              <BaSeButton
                onClick={() => {
                  const slug = BP_SLUG_BY_KEY[currentMapping.balanceKey]
                  if (slug) ApiGeneral().sendDigitalMoments(slug, { plan_id: planId, cnpj: currentCnpj })
                  onNext()
                }}
                type="primary"
                size="small"
                color="#005EB8"
                value="Salvar e continuar"
                isDisabled={invalidForm() || isSaving || loading || disabledFields}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default BusinessAccordion;
